// TODO: AI geenerated sunlight scoring system, to be reviewed and adjusted

import { DailySunlightResult } from '@/types/sunlight';

export interface SunlightScoreBreakdown {
	totalScore: number; // Final score (0-100)
	winterScore: number; // Score for winter months (0-30)
	summerScore: number; // Score for summer months (0-25)
	springFallScore: number; // Score for spring/fall months (0-25)
	consistencyScore: number; // Score for year-round consistency (0-10)
	qualityScore: number; // Score for sunlight quality/duration (0-10)
	averageHours: number; // Average daily sunlight hours across all points
	minHours: number; // Minimum average hours (worst point)
	maxHours: number; // Maximum average hours (best point)
	winterAverage: number; // Average hours in winter across all points
	summerAverage: number; // Average hours in summer across all points
	daylightAutonomy: number; // Average daylight autonomy percentage
	pointsAnalyzed: number; // Number of points analyzed
}

/**
 * Calculate sunlight quality score based on point sunlight analysis results
 *
 * Scoring criteria:
 * - Winter sunlight: 0-30 points (most critical for well-being)
 * - Summer sunlight: 0-25 points (can be too much)
 * - Spring/Fall: 0-25 points (balanced seasons)
 * - Consistency: 0-10 points (stable sunlight throughout year and across points)
 * - Quality: 0-10 points (duration above minimum thresholds, daylight autonomy)
 *
 * Optimal ranges:
 * - Winter: 2-4 hours minimum
 * - Summer: 4-8 hours optimal (too much can cause overheating)
 * - Spring/Fall: 3-6 hours optimal
 * - Daylight Autonomy: >50% for working hours
 */
export const calculateSunlightScore = (
	sunlightData: DailySunlightResult[]
): SunlightScoreBreakdown => {
	if (!sunlightData || sunlightData.length === 0) {
		return {
			totalScore: 0,
			winterScore: 0,
			summerScore: 0,
			springFallScore: 0,
			consistencyScore: 0,
			qualityScore: 0,
			averageHours: 0,
			minHours: 0,
			maxHours: 0,
			winterAverage: 0,
			summerAverage: 0,
			daylightAutonomy: 0,
			pointsAnalyzed: 0,
		};
	}

	// Aggregate metrics across all points
	let totalAverageHours = 0;
	let totalWinterHours = 0;
	let totalSummerHours = 0;
	let totalSpringHours = 0;
	let totalFallHours = 0;
	let totalDaylightAutonomy = 0;
	let totalConsistency = 0;
	// let totalZeroDays = 0;
	let totalViableDays = 0;

	let minAverageHours = Infinity;
	let maxAverageHours = 0;

	sunlightData.forEach((point) => {
		totalAverageHours += point.averageDailySunlightHours || 0;
		totalWinterHours += point.winterMonthsAverage || 0;
		totalSummerHours += point.summerMonthsAverage || 0;
		totalSpringHours += point.springMonthsAverage || 0;
		totalFallHours += point.fallMonthsAverage || 0;
		totalDaylightAutonomy += point.daylightAutonomyPercent || 0;
		totalConsistency += point.consistencyScore || 0;
		// totalZeroDays += point.zeroDaysCount;
		totalViableDays += point.minViableDaysCount || 0;

		minAverageHours = Math.min(minAverageHours, point.averageDailySunlightHours || 0);
		maxAverageHours = Math.max(maxAverageHours, point.averageDailySunlightHours || 0);
	});

	const numPoints = sunlightData.length;
	const averageHours = numPoints > 0 ? totalAverageHours / numPoints : 0;
	const winterAverage = numPoints > 0 ? totalWinterHours / numPoints : 0;
	const summerAverage = numPoints > 0 ? totalSummerHours / numPoints : 0;
	const springAverage = numPoints > 0 ? totalSpringHours / numPoints : 0;
	const fallAverage = numPoints > 0 ? totalFallHours / numPoints : 0;
	const daylightAutonomy = numPoints > 0 ? totalDaylightAutonomy / numPoints : 0;
	const avgConsistency = numPoints > 0 ? totalConsistency / numPoints : 0;
	// const avgZeroDays = totalZeroDays / numPoints;
	const avgViableDays = numPoints > 0 ? totalViableDays / numPoints : 0;

	// Calculate Winter Score (0-30 points) - most critical
	// Optimal: 2-4 hours minimum in winter
	let winterScore = 0;
	if (winterAverage >= 4) {
		winterScore = 30;
	} else if (winterAverage >= 2) {
		// Linear scale from 2-4 hours
		winterScore = ((winterAverage - 2) / 2) * 30;
	} else if (winterAverage >= 1) {
		// Partial credit for 1-2 hours
		winterScore = ((winterAverage - 1) / 1) * 15;
	}
	// Below 1 hour = 0 points

	// Penalize if minimum point is too low (ensures all points get some winter sun)
	const worstWinterPoint = Math.min(...sunlightData.map((p) => p.winterMonthsAverage));
	if (worstWinterPoint < 1) {
		winterScore *= 0.7; // 30% penalty if any point gets less than 1 hour in winter
	}

	// Calculate Summer Score (0-25 points)
	// Optimal: 4-8 hours (too much can cause overheating)
	let summerScore = 0;
	if (summerAverage >= 4 && summerAverage <= 8) {
		summerScore = 25;
	} else if (summerAverage > 8) {
		// Penalize excessive summer sun (overheating)
		const excess = summerAverage - 8;
		summerScore = Math.max(0, 25 - excess * 2);
	} else if (summerAverage >= 2) {
		// Scale from 2-4 hours
		summerScore = ((summerAverage - 2) / 2) * 25;
	} else {
		// Below 2 hours - minimal score
		summerScore = (summerAverage / 2) * 10;
	}

	// Calculate Spring/Fall Score (0-25 points)
	// Optimal: 3-6 hours
	const springFallAverage = (springAverage + fallAverage) / 2;
	let springFallScore = 0;
	if (springFallAverage >= 3 && springFallAverage <= 6) {
		springFallScore = 25;
	} else if (springFallAverage > 6) {
		// Still good but slightly less optimal
		springFallScore = Math.max(15, 25 - (springFallAverage - 6));
	} else if (springFallAverage >= 2) {
		// Scale from 2-3 hours
		springFallScore = ((springFallAverage - 2) / 1) * 25;
	} else {
		springFallScore = (springFallAverage / 2) * 12;
	}

	// Calculate Consistency Score (0-10 points)
	// Based on:
	// 1. Temporal consistency (already calculated per point)
	// 2. Spatial consistency (variance between points)
	const temporalConsistency = avgConsistency; // Already 0-1 scale

	// Calculate spatial variance
	const pointAverages = sunlightData.map((p) => p.averageDailySunlightHours || 0);
	const spatialVariance =
		numPoints > 0
			? pointAverages.reduce((sum, val) => sum + Math.pow(val - averageHours, 2), 0) /
				numPoints
			: 0;
	const spatialStdDev = Math.sqrt(spatialVariance);

	// Lower spatial std dev = better consistency across the space
	let spatialConsistency = 0;
	if (spatialStdDev <= 0.5) {
		spatialConsistency = 1;
	} else if (spatialStdDev <= 2) {
		spatialConsistency = 1 - (spatialStdDev - 0.5) / 1.5;
	}
	// Above 2 hours std dev = 0

	// Combine temporal and spatial consistency (60% temporal, 40% spatial)
	const consistencyScore = (temporalConsistency * 0.6 + spatialConsistency * 0.4) * 10;

	// Calculate Quality Score (0-10 points)
	// Based on:
	// 1. Daylight autonomy (50%)
	// 2. Viable days count (30%)
	// 3. Minimum hours threshold (20%)
	let qualityScore = 0;

	// Daylight Autonomy component (0-5 points)
	// Target: >50% for good quality
	if (daylightAutonomy >= 75) {
		qualityScore += 5;
	} else if (daylightAutonomy >= 50) {
		qualityScore += 3 + ((daylightAutonomy - 50) / 25) * 2;
	} else if (daylightAutonomy >= 25) {
		qualityScore += ((daylightAutonomy - 25) / 25) * 3;
	}

	// Viable days component (0-3 points)
	// Based on percentage of days with >4 hours
	const totalDays = sunlightData[0]?.totalDaysAnalyzed || 365;
	const viableDaysPercent = (avgViableDays / totalDays) * 100;
	if (viableDaysPercent >= 70) {
		qualityScore += 3;
	} else if (viableDaysPercent >= 40) {
		qualityScore += ((viableDaysPercent - 40) / 30) * 3;
	}

	// Minimum hours component (0-2 points)
	// Ensures worst point still gets some light
	if (minAverageHours >= 2) {
		qualityScore += 2;
	} else if (minAverageHours >= 1) {
		qualityScore += minAverageHours - 1;
	}

	// Calculate total score
	const totalScore = Math.round(
		winterScore + summerScore + springFallScore + consistencyScore + qualityScore
	);

	return {
		totalScore: Math.max(0, Math.min(100, isNaN(totalScore) ? 0 : totalScore)) / 10,
		winterScore: isNaN(winterScore) ? 0 : Math.round(winterScore * 10) / 10,
		summerScore: isNaN(summerScore) ? 0 : Math.round(summerScore * 10) / 10,
		springFallScore: isNaN(springFallScore) ? 0 : Math.round(springFallScore * 10) / 10,
		consistencyScore: isNaN(consistencyScore) ? 0 : Math.round(consistencyScore * 10) / 10,
		qualityScore: isNaN(qualityScore) ? 0 : Math.round(qualityScore * 10) / 10,
		averageHours: isNaN(averageHours) ? 0 : Math.round(averageHours * 10) / 10,
		minHours:
			isNaN(minAverageHours) || minAverageHours === Infinity
				? 0
				: Math.round(minAverageHours * 10) / 10,
		maxHours: isNaN(maxAverageHours) ? 0 : Math.round(maxAverageHours * 10) / 10,
		winterAverage: isNaN(winterAverage) ? 0 : Math.round(winterAverage * 10) / 10,
		summerAverage: isNaN(summerAverage) ? 0 : Math.round(summerAverage * 10) / 10,
		daylightAutonomy: isNaN(daylightAutonomy) ? 0 : Math.round(daylightAutonomy * 10) / 10,
		pointsAnalyzed: numPoints,
	};
};
